import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const MobileWaffleGrid = ({ stablecoinPercentage, m2Percentage }) => {
  const [touchedTile, setTouchedTile] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const totalTiles = 100;

  const tiles = useMemo(() => {
    // Calculate exact tiles needed for accurate fractional representation
    const exactTiles = (stablecoinPercentage / 100) * totalTiles;
    const fullTiles = Math.floor(exactTiles);
    const frac = exactTiles - fullTiles;
    
    // Generate tiles with bottom-up filling order
    // Bottom row (indices 90-99), then next row up (80-89), etc.
    const tileOrder = [];
    for (let row = 9; row >= 0; row--) {
      for (let col = 0; col < 10; col++) {
        tileOrder?.push(row * 10 + col);
      }
    }
    
    return Array.from({ length: totalTiles }, (_, visualIndex) => {
      const fillIndex = tileOrder?.indexOf(visualIndex);
      
      let type, fillAmount;
      if (fillIndex < fullTiles) {
        type = 'stablecoin';
        fillAmount = 1; // Fully filled
      } else if (fillIndex === fullTiles && frac > 0) {
        type = 'partial';
        fillAmount = frac; // Partially filled
      } else {
        type = 'm2';
        fillAmount = 0; // Empty (gray)
      }
      
      return {
        id: visualIndex,
        type,
        fillAmount,
        color: type === 'm2' ? '#D9D9D9' : '#D08A2A'
      };
    });
  }, [stablecoinPercentage, totalTiles]);

  const handleTileTouch = (tile) => {
    setTouchedTile(tile);
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000);
  };

  return (
    <div className="relative w-full">
      <div className="grid grid-cols-10 gap-1 md:gap-1.5 lg:gap-2 p-4 md:p-6 lg:p-8">
        {tiles?.map((tile) => (
          <button
            key={tile?.id}
            onClick={() => handleTileTouch(tile)}
            className="aspect-square rounded transition-all duration-300 active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 relative overflow-hidden"
            style={{
              backgroundColor: tile?.type === 'm2' ? '#D9D9D9' : 'transparent'
            }}
            aria-label={`${tile?.type === 'stablecoin' ? 'Stablecoin' : tile?.type === 'partial' ? 'Partial stablecoin' : 'M2'} tile ${tile?.id + 1}`}
          >
            {tile?.type === 'stablecoin' && (
              <div 
                className="absolute bottom-0 left-0 right-0 bg-[#D08A2A]"
                style={{ height: '100%' }}
              />
            )}
            {tile?.type === 'partial' && (
              <>
                <div className="absolute inset-0 bg-[#D9D9D9]" />
                <div 
                  className="absolute bottom-0 left-0 right-0 bg-[#D08A2A]"
                  style={{ height: `${tile?.fillAmount * 100}%` }}
                />
              </>
            )}
          </button>
        ))}
      </div>
      {showTooltip && touchedTile && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-card border-2 border-border rounded-lg shadow-2xl p-4 z-50 max-w-xs w-11/12 animate-fade-in">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: touchedTile?.color }}
              />
              <span className="text-sm font-semibold text-foreground">
                {touchedTile?.type === 'stablecoin' ? 'Stablecoin' : touchedTile?.type === 'partial' ? 'Partial Stablecoin' : 'M2 Money Supply'}
              </span>
            </div>
            <button
              onClick={() => setShowTooltip(false)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close tooltip"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {touchedTile?.type === 'stablecoin'
              ? `Tile #${touchedTile?.id + 1} - Fully filled with stablecoins`
              : touchedTile?.type === 'partial'
              ? `Tile #${touchedTile?.id + 1} - ${(touchedTile?.fillAmount * 100)?.toFixed(1)}% filled`
              : `Tile #${touchedTile?.id + 1} - Traditional M2 money supply`}
          </p>
        </div>
      )}
    </div>
  );
};

export default MobileWaffleGrid;